import {
  OntologyRolodexEntryType,
  RD_QUERY_LIMIT,
} from 'features/dynamicOntology'
import { RolodexEntryQueryBody, useRolodexHistogram } from 'features/rolodex'
import chunk from 'lodash/chunk'
import flatMap from 'lodash/flatMap'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import memoize from 'lodash/memoize'
import uniq from 'lodash/uniq'
import { useCallback, useMemo } from 'react'
import {
  GeckoEntry,
  RolodexEntryType,
  RolodexHistogramFilter,
  RolodexHistogramResponse,
} from 'types'

import { useRolodexIds } from './useRolodexIds'

export type RolodexPaginationType = {
  pageSize: number
  currentPage: number
}

export function optionalAddToArrayAndUniq<T>(
  array: ReadonlyArray<T> | undefined,
  item: T | undefined
): T[] {
  return uniq([...(array ?? []), ...(item ? [item] : [])])
}

export type UsePaginatedQueryParams = {
  query?: RolodexEntryQueryBody
  pagination: RolodexPaginationType
  groupByType?: OntologyRolodexEntryType
}

export type UsePaginatedQueryResult = {
  query: RolodexEntryQueryBody | undefined
  isLoading: boolean
  totalEntries: number
  sorter: (a: GeckoEntry, b: GeckoEntry) => number
  truncated: boolean
  /**
   * The full ordered list of group IDs that match the query, across all pages.
   * Empty before the histogram + ID queries resolve. Useful for batched
   * exports where the consumer needs to load every page.
   *
   * Example when grouping inspections by unit:
   * `['unit-1', 'unit-2']`
   */
  orderedGroupIds: ReadonlyArray<string>
  /**
   * Mapping from each group ID to the base IDs it represents. When the
   * `groupByType` matches the query type each group ID maps to a single
   * base ID, but for cross-type group-bys (e.g. group inspections by unit)
   * a single group ID can fan out to several base IDs.
   *
   * Example when grouping inspections by unit:
   * `unit-1 -> ['inspection-7', 'inspection-9']`
   */
  groupIdToBaseId: ReadonlyMap<string, ReadonlyArray<string>>
  /**
   * The upward-link entry types resolved for this query. Consumers that
   * issue their own `/entries/load` calls (e.g. an export) should pass
   * these through so each loaded entry comes back with the same linked
   * entries as the paginated UI.
   */
  links: ReadonlyArray<RolodexEntryType>
}

type NormalizedPaginatedQueryParam = {
  pagination: RolodexPaginationType
  query: RolodexEntryQueryBody
  groupByType?: OntologyRolodexEntryType
}

export function usePaginatedQueries(
  params: ReadonlyArray<UsePaginatedQueryParams> = []
): ReadonlyArray<UsePaginatedQueryResult> {
  const normalizedParams = useMemo(
    (): ReadonlyArray<NormalizedPaginatedQueryParam> =>
      params.map((param) => ({
        pagination: param.pagination,
        query: param.query ?? {},
        groupByType: param.groupByType,
      })),
    [params]
  )

  const queriesWithGroup = useMemo(
    () =>
      normalizedParams.map(({ query, groupByType }) => ({
        ...query,
        links: optionalAddToArrayAndUniq(query.links, groupByType).filter(
          (t) => !!t && t !== query.type
        ),
      })),
    [normalizedParams]
  )

  const addIdsFilter = useCallback(
    (ids: string[] | undefined, filters: RolodexEntryQueryBody['filters']) => {
      return [
        ...(filters ?? []),
        ...(ids ? [{ field: 'id', op: 'in', value: ids }] : []),
      ] as undefined | RolodexHistogramFilter[]
    },
    []
  )

  const histogramQueries = useMemo(
    () =>
      queriesWithGroup.map((queryWithGroup, index) => {
        const { groupByType } = normalizedParams[index]
        const groupByField =
          groupByType && groupByType !== queryWithGroup.type
            ? `tags.${groupByType}`
            : 'id'

        return {
          ...queryWithGroup,
          filters: addIdsFilter(queryWithGroup.ids, queryWithGroup.filters),
          select: [
            {
              field: 'id',
              label: 'baseId',
              group_by: true,
            },
            {
              field: groupByField,
              label: 'groupById',
              group_by: true,
            },
          ],
          links: optionalAddToArrayAndUniq(
            queryWithGroup?.links,
            groupByType
          ).filter((t) => t !== queryWithGroup.type) as RolodexEntryType[],
          type: queryWithGroup.type,
          limit: RD_QUERY_LIMIT,
          advanced_filter: undefined, // Histogram queries use a different filter type from the load queries, but this should likely be merged at some point
        }
      }),
    [addIdsFilter, normalizedParams, queriesWithGroup]
  )

  const { data: groupingData, isLoading: groupingLoading } =
    useRolodexHistogram(histogramQueries)

  const groupIdToBaseIdList = useMemo(
    (): ReadonlyArray<ReadonlyMap<string, ReadonlyArray<string>>> =>
      normalizedParams.map((_param, index) => {
        const dataForIndex: RolodexHistogramResponse =
          groupingData?.[index] ?? []
        const groupingDataRecs: ReadonlyArray<{
          id: string
          groupById: string
        }> = dataForIndex
          .flatMap((rec) => ({
            id: rec['baseId'] as string,
            groupById: rec['groupById'] as string,
          }))
          .filter(
            (item): item is NonNullable<typeof item> =>
              !isNil(item) && !isNil(item.id) && !isNil(item.groupById)
          )

        const groupIdToOrderedId = new Map<string, string[]>()
        groupingDataRecs.forEach((rec) => {
          const arr = groupIdToOrderedId.get(rec.groupById) ?? []
          groupIdToOrderedId.set(rec.groupById, arr)
          arr.push(rec.id)
        })
        return groupIdToOrderedId
      }),
    [groupingData, normalizedParams]
  )

  const rolodexIdQueryPrep = useMemo(() => {
    const queries: RolodexEntryQueryBody[] = []
    const indexMap: number[] = []
    const truncated: boolean[] = []

    normalizedParams.forEach(({ groupByType, query }, index) => {
      const groupIds = [...groupIdToBaseIdList[index].keys()]
      if (!groupIds.length) return
      const trunc = groupIds.length >= RD_QUERY_LIMIT
      if (trunc) {
        console.warn(
          `RD_ID_LIMIT reached for Rolodex query group ${groupIds.join(', ')}. Will truncate the group.`
        )
      }

      const links = uniq(
        query.order_by
          ?.map((o) => o.type as RolodexEntryType | undefined)
          .filter((t): t is NonNullable<typeof t> => !!t)
          .filter((t) => t !== groupByType) ?? undefined
      )

      queries.push({
        ids: trunc ? groupIds.slice(0, RD_QUERY_LIMIT) : groupIds,
        type: groupByType as RolodexEntryType,
        links: links?.length ? links : undefined,
        order_by: query.order_by,
      })
      indexMap.push(index)
      truncated.push(trunc)
    })

    return { queries, indexMap, truncated }
  }, [groupIdToBaseIdList, normalizedParams])

  const {
    idsByQuery: orderedGroupIdsByQuery,
    isLoading: orderedGroupIdsLoading,
  } = useRolodexIds(rolodexIdQueryPrep.queries)

  const orderedGroupIdsByIndex: ReadonlyArray<
    ReadonlyArray<string> | undefined
  > = useMemo(() => {
    if (!normalizedParams.length) {
      return [] as ReadonlyArray<ReadonlyArray<string> | undefined>
    }

    const idsByIndex: Array<ReadonlyArray<string> | undefined> =
      normalizedParams.map(() => undefined)

    orderedGroupIdsByQuery?.forEach((ids, idx) => {
      const originalIndex = rolodexIdQueryPrep.indexMap[idx]
      if (isNumber(originalIndex)) {
        idsByIndex[originalIndex] = ids
      }
    })

    return idsByIndex
  }, [normalizedParams, orderedGroupIdsByQuery, rolodexIdQueryPrep.indexMap])

  const entryQueries = useMemo(
    () =>
      normalizedParams.map((param, index) => {
        const groupIdToBaseId = groupIdToBaseIdList[index]
        const orderedGroupIds = orderedGroupIdsByIndex[index] ?? []
        const pageGroups = chunk(
          orderedGroupIds,
          param.pagination?.pageSize ?? 10
        )
        const pages = pageGroups.map((groups) =>
          flatMap(groups, (group) => groupIdToBaseId.get(group) ?? [])
        )
        const currPage = (param.pagination?.currentPage ?? 1) - 1
        const thisPageIds =
          (currPage < pages.length ? pages[currPage] : []) ?? []

        const links = uniq([
          ...optionalAddToArrayAndUniq(
            queriesWithGroup[index]?.links,
            param.groupByType
          ),
        ]) as RolodexEntryType[]

        return {
          ids: [...thisPageIds].filter((id) => !isNil(id)),
          links,
          order_by: param.query.order_by,
          truncated: rolodexIdQueryPrep.truncated[index],
        }
      }),
    [
      groupIdToBaseIdList,
      normalizedParams,
      orderedGroupIdsByIndex,
      queriesWithGroup,
      rolodexIdQueryPrep.truncated,
    ]
  )

  const groupIdPositions = useMemo<
    ReadonlyArray<() => ReadonlyMap<string, number>>
  >(
    () =>
      normalizedParams.map((_param, index) =>
        memoize(
          () =>
            new Map(
              (orderedGroupIdsByIndex[index] ?? []).map((id, idx) => [id, idx])
            )
        )
      ),
    [normalizedParams, orderedGroupIdsByIndex]
  )

  return useMemo(
    () =>
      normalizedParams.map((_, index) => ({
        query: entryQueries[index],
        isLoading: orderedGroupIdsLoading || groupingLoading,
        totalEntries: orderedGroupIdsByIndex[index]?.length ?? 0,
        sorter: (a: GeckoEntry, b: GeckoEntry) => {
          const positions = groupIdPositions[index]()
          const posA = positions.get(a.id) ?? Number.MAX_VALUE
          const posB = positions.get(b.id) ?? Number.MAX_VALUE
          return posA - posB
        },
        truncated: rolodexIdQueryPrep.truncated[index],
        orderedGroupIds: orderedGroupIdsByIndex[index] ?? [],
        groupIdToBaseId: groupIdToBaseIdList[index],
        links: entryQueries[index]?.links ?? [],
      })),
    [
      entryQueries,
      groupIdPositions,
      groupIdToBaseIdList,
      groupingLoading,
      normalizedParams,
      orderedGroupIdsByIndex,
      orderedGroupIdsLoading,
      rolodexIdQueryPrep.truncated,
    ]
  )
}

export function usePaginatedQuery(
  query: RolodexEntryQueryBody = {},
  pagination: RolodexPaginationType,
  groupByType?: OntologyRolodexEntryType
): UsePaginatedQueryResult {
  const [result] = usePaginatedQueries([{ query, pagination, groupByType }])

  return (
    result ?? {
      query: undefined,
      isLoading: false,
      totalEntries: 0,
      sorter: () => 0,
      truncated: false,
      orderedGroupIds: [],
      groupIdToBaseId: new Map(),
      links: [],
    }
  )
}
